// Client-side auth helpers. The source of truth is the server's session cookie
// (HttpOnly, SameSite=Strict). These helpers simply ask the API "who am I?"
// and provide convenience wrappers for route loaders + components.

import { useRouteLoaderData, redirect, type LoaderFunctionArgs } from 'react-router';
import { ROLES, type Role } from '@besideu/shared/roles';
import { api, ApiError } from './api.ts';

// Mirrors the API's `SessionClaims` discriminated union (apps/api/src/env.ts).
// BesideU Admin sessions omit `organizationId` per DESIGN §4.4.
// CGC member sessions also omit `organizationId` per DESIGN §5.5 (Slice 12).
export type SessionClaims = TenantSessionClaims | AdminSessionClaims | CgcSessionClaims;
export interface TenantSessionClaims {
  userId: string;
  organizationId: string;
  role: 'HR_Admin' | 'HR_Viewer' | 'Manager' | 'Employee';
  // Phase 0.2 — JWT `name` claim. CGC-gated for first-name display on
  // the Maeve-First greeting (per PROJECT §"Mobile-First Maeve Entry").
  // Empty string if the JWT predates the slice or the user row has no
  // name. UI gates the spec greeting on a non-empty value; the
  // fallback copy is the neutral, anti-anthropomorphism-safe version
  // (see `EmployeeMaeveFirst.tsx` / `ManagerMaeveFirst.tsx`), so a
  // missing name is non-regressive.
  name: string;
}
export interface AdminSessionClaims {
  userId: string;
  role: 'BesideU_Admin';
}
export interface CgcSessionClaims {
  userId: string;
  role: 'CGC_Member';
}

interface MeResponse {
  session: SessionClaims;
}

export async function fetchMe(): Promise<SessionClaims | null> {
  try {
    const { session } = await api.get<MeResponse>('/api/me');
    return session;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

// Deliberately NOT first-sign-in-aware, unlike the backend's own
// `landingPathFor` (apps/api/src/routes/auth.ts) — this one is used by
// secondary/edge-case redirects (an already-authenticated visitor reloading
// `/sign-in` in a second tab, or `requireRoles`'s role-mismatch bounce), not
// the primary magic-link-click flow (which navigates via the `/auth/verify`
// POST response's `landingPath` field directly — see `Verify.tsx`). A
// first-time Manager whose ONLY path to a landing decision goes through one
// of these secondary redirects could occasionally skip `/manager/welcome`;
// this is the same accepted, self-resolving tradeoff as closing the tab
// mid-walkthrough (see the backend comment at the `firstSignInAt` stamp) —
// not worth threading first-sign-in state through every redirect path for.
export function landingPathFor(role: Role): string {
  switch (role) {
    case 'HR_Admin':
    case 'HR_Viewer':
      return '/hr';
    case 'Manager':
      return '/manager';
    case 'Employee':
      return '/portal';
    case 'BesideU_Admin':
      return '/admin';
    case 'CGC_Member':
      return '/cgc';
  }
}

// Route loader factory — redirects to `unauthPath` if unauthenticated, or to
// the caller's role-appropriate landing page if their role isn't in `allowed`.
// `unauthPath` defaults to `/sign-in` (tenant); the BesideU Admin and CGC
// tiers pass their own dedicated entry points so an expired session lands on
// the right magic-link + TOTP flow rather than the tenant page (which has no
// path back into the admin / CGC auth flow).
// `redirect()` returns a Response that React Router catches when thrown — the
// `only-throw-error` rule disable below is the documented data-router idiom.
//
// The returned `SessionClaims` is consumed by child components via
// `useSession(routeId)` so we don't refetch /me from inside React.
export function requireRoles(allowed: readonly Role[], unauthPath: string = '/sign-in') {
  return async (): Promise<SessionClaims> => {
    const session = await fetchMe();
    /* eslint-disable @typescript-eslint/only-throw-error -- React Router data-router redirect idiom */
    if (!session) throw redirect(unauthPath);
    if (!allowed.includes(session.role)) throw redirect(landingPathFor(session.role));
    /* eslint-enable @typescript-eslint/only-throw-error */
    return session;
  };
}

// Permanent client-side redirect, as a loader. Used to keep retired paths
// alive: the Manager bottom-nav shell used to live on its own UA-gated
// `/manager/mobile/*` tree before it became a responsive layer over
// `/manager/*` (see `ManagerShell.tsx`). Those old paths are in the wild —
// sent in touch-point reminder emails, bookmarked on managers' phones, and
// returned as the sign-in `landingPath` by any API build older than the
// deploy that pairs with this one — so they forward instead of 404ing.
// `to` may be derived from the loader args to carry a `:id` across.
//
// The incoming query string and fragment are carried onto the target. A
// retired path is by definition one someone else's link points at, and those
// links carry state we don't own: campaign/source params on an email
// deep-link, and the `#`-anchors the mobile landings used before their chats
// became tabs. Silently dropping them turns a working link into a subtly
// wrong one, which is worse than a 404 because nothing surfaces the loss.
// `to` must therefore be a bare path — a caller that ever needs its own query
// has to merge, not concatenate.
//
// The fragment needs `window.location`, not `args.request`: a data-router
// builds a client-side loader `Request` from pathname + search only, because
// a fragment is never sent to a server, so `new URL(request.url).hash` is
// always empty here. `window.location.hash` is only OURS to copy when the
// browser is already sitting on the URL being loaded — true for the case that
// actually matters (a cold load from an email link or a bookmark) and false
// mid-client-navigation, where it would still hold the PREVIOUS page's
// fragment and we'd graft it onto an unrelated target. The pathname guard is
// what distinguishes the two.
export function redirectLoader(to: string | ((args: LoaderFunctionArgs) => string)) {
  return (args: LoaderFunctionArgs): never => {
    const target = typeof to === 'function' ? to(args) : to;
    const { pathname, search } = new URL(args.request.url);
    const hash =
      typeof window !== 'undefined' && window.location.pathname === pathname
        ? window.location.hash
        : '';
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- data-router redirect idiom
    throw redirect(`${target}${search}${hash}`);
  };
}

// Read the session from a parent route's loader data. Components inside a
// loader-protected route get the session that the loader fetched at navigation
// time, with no second /me round-trip. The routeId is the `id` set on the
// parent route in router.tsx (currently 'hr-shell' / 'manager-shell' /
// 'employee-portal').
export function useSession(routeId: string): SessionClaims {
  const data: unknown = useRouteLoaderData(routeId);
  if (!isSessionClaims(data)) {
    // The loader either didn't run or didn't return a session — should be
    // impossible if the routeId matches a `requireRoles`-loaded route.
    throw new Error(`useSession(${routeId}): no session in route loader data`);
  }
  return data;
}

function isSessionClaims(d: unknown): d is SessionClaims {
  if (!d || typeof d !== 'object') return false;
  const r = d as Record<string, unknown>;
  if (typeof r.userId !== 'string' || typeof r.role !== 'string') return false;
  if (!(ROLES as readonly string[]).includes(r.role)) return false;
  // Admin AND CGC sessions omit `organizationId`; tenant sessions require it.
  if (r.role === 'BesideU_Admin' || r.role === 'CGC_Member') {
    return r.organizationId === undefined;
  }
  if (typeof r.organizationId !== 'string') return false;
  // Phase 0.2 — `name` is optional on the wire for backward compat
  // (any pre-Phase-0.2 token in flight still verifies). Default to
  // empty string so downstream code can `name || fallback` without
  // a null check.
  if (r.name !== undefined && typeof r.name !== 'string') return false;
  return true;
}
